// frontend/src/internal/useInternalGuard.js
// 既存ページの書き込み操作前に呼ぶガードフック。
//
// 使い方:
//   const guard = useInternalGuard();
//   const handleSave = async () => {
//     if (!guard.allow('shift_patterns', { id: pattern.id, changes: form })) return;
//     await supabase.from('shift_patterns').update(form).eq('id', pattern.id);
//   };
//
// 動作:
//   - 自社スタッフでない通常ユーザー → 常に true (素通り)
//   - 自社スタッフかつ編集モード OFF → false + 警告通知 + 監査ログ (denied)
//   - 自社スタッフかつ編集モード ON → true + 監査ログ (write_op)

import { useCallback } from 'react';
import { useInternalStaff } from '../contexts/InternalStaffContext';

export const useInternalGuard = () => {
  const { internalStaff, canEdit, logAudit, editSession } = useInternalStaff();

  const allow = useCallback((target, detail = {}) => {
    // 通常ユーザー (自社スタッフでない) は影響なし
    if (!internalStaff) return true;

    if (!canEdit) {
      // 閲覧モード → 書き込みブロック
      // eslint-disable-next-line no-alert
      alert(
        '🔒 閲覧モードのため書き込みできません。\n'
        + '編集モードを ON にしてから再操作してください。\n'
        + '(Internal ダッシュボード → 編集モードを ON)'
      );
      logAudit('write_op_denied', {
        target,
        detail,
        reason: 'edit_mode_off',
      });
      return false;
    }

    // 編集モード ON → 通過 + 監査
    logAudit('write_op', {
      target,
      detail,
      reason: editSession?.reason,
    });
    return true;
  }, [internalStaff, canEdit, editSession, logAudit]);

  return { allow, isInternal: !!internalStaff, canEdit };
};
